import React, { useState } from 'react';
import { Link, useNavigate, useLocation } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useAuth } from '../contexts/AuthContext';
import AuthLayout from '../components/auth/AuthLayout';
import FormInput from '../components/auth/FormInput';
import SocialButtons from '../components/auth/SocialButtons';
import { useGoogleAuth } from '../hooks/useGoogleAuth';

export default function LoginPage() {
  const { t } = useTranslation();
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [rememberMe, setRememberMe] = useState(true);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [formError, setFormError] = useState('');

  const { login, error, clearError } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();

  const from = location.state?.from?.pathname || '/v4';

  // Google Identity Services - Popup mode
  const { signIn: googleSignIn, isLoading: googleLoading } = useGoogleAuth({
    onSuccess: (data) => {
      console.log('✅ Google login success:', data);
      // Store tokens
      localStorage.setItem('pilotleague_token', data.token);
      localStorage.setItem('pilotleague_refresh_token', data.refreshToken);
      // Redirect to dashboard
      window.location.href = '/v4';
    },
    onError: (error) => {
      console.error('❌ Google login error:', error);
      setFormError(error.message || t('auth.login.errors.googleError'));
    },
  });

  const handleSubmit = async (e) => {
    e.preventDefault();
    setFormError('');
    clearError();

    if (!email || !password) {
      setFormError(t('auth.login.errors.fillAllFields'));
      return;
    }

    setIsSubmitting(true);
    try {
      await login(email, password, rememberMe);
      navigate(from, { replace: true });
    } catch (err) {
      setFormError(err.message || t('auth.login.errors.invalidCredentials'));
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleGoogleLogin = () => {
    setFormError('');
    googleSignIn();
  };

  const features = [
    {
      icon: '📊',
      title: t('auth.features.login.scoring.title'),
      description: t('auth.features.login.scoring.description'),
    },
    {
      icon: '🏆',
      title: t('auth.features.login.ranking.title'),
      description: t('auth.features.login.ranking.description'),
    },
    {
      icon: '📈',
      title: t('auth.features.login.progress.title'),
      description: t('auth.features.login.progress.description'),
    },
  ];

  return (
    <AuthLayout tagline={t('auth.taglines.login')} features={features}>
      <div className="auth-form-header">
        <h1 className="auth-form-title">{t('auth.login.title')}</h1>
        <p className="auth-form-subtitle">{t('auth.login.subtitle')}</p>
      </div>

      {(formError || error) && (
        <div className="form-alert form-alert-error">
          <svg viewBox="0 0 24 24" className="form-alert-icon">
            <path
              d="M12 2C6.48 2 2 6.48 2 12s4.48 10 10 10 10-4.48 10-10S17.52 2 12 2zm1 15h-2v-2h2v2zm0-4h-2V7h2v6z"
              fill="currentColor"
            />
          </svg>
          <span>{formError || error}</span>
        </div>
      )}

      <form onSubmit={handleSubmit}>
        <FormInput
          label={t('auth.login.emailOrUsername')}
          type="text"
          value={email}
          onChange={(e) => setEmail(e.target.value)}
          placeholder={t('auth.login.emailPlaceholder')}
          icon="user"
          required
          autoComplete="email"
        />

        <FormInput
          label={t('auth.login.password')}
          type="password"
          value={password}
          onChange={(e) => setPassword(e.target.value)}
          placeholder={t('auth.login.passwordPlaceholder')}
          icon="lock"
          required
          autoComplete="current-password"
        />

        <div className="form-row">
          <label className="form-checkbox">
            <input
              type="checkbox"
              checked={rememberMe}
              onChange={(e) => setRememberMe(e.target.checked)}
            />
            <span className="checkbox-custom">
              <svg viewBox="0 0 24 24">
                <path d="M9 16.17L4.83 12l-1.42 1.41L9 19 21 7l-1.41-1.41z" fill="currentColor" />
              </svg>
            </span>
            {t('auth.login.rememberMe')}
          </label>
          <Link to="/forgot-password" className="form-link">
            {t('auth.login.forgotPassword')}
          </Link>
        </div>

        <button type="submit" className="btn btn-primary" disabled={isSubmitting}>
          {isSubmitting ? (
            <>
              <span className="btn-spinner" />
              {t('auth.login.submitting')}
            </>
          ) : (
            <>
              <svg viewBox="0 0 24 24">
                <path d="M10 17l5-5-5-5v10z" fill="currentColor" />
              </svg>
              {t('auth.login.submit')}
            </>
          )}
        </button>
      </form>

      <div className="form-divider">
        <span>{t('auth.login.orContinueWith')}</span>
      </div>

      <SocialButtons onGoogleClick={handleGoogleLogin} />

      <div className="auth-footer">
        {t('auth.login.noAccount')} <Link to="/register">{t('auth.login.createAccount')}</Link>
      </div>
    </AuthLayout>
  );
}
